import * as PIXI from 'pixi.js';
import { AgentManager } from './agent-manager.js';
import { IsometricCharacter } from './isometric-character.js';
import { FurnitureManager } from './furniture-manager.js';
import { OFFICE_FURNITURE_LAYOUT, WALKABLE_MATRIX } from './furniture-layout.js';
import { WalkableGrid } from './walkable-grid.js';

/**
 * Isometric Rendering Engine
 * Uses 2:1 pixel ratio dimetric projection
 */

// Configuration
const CONFIG = {
    TILE_WIDTH: 64,      // Width of isometric tile
    TILE_HEIGHT: 32,     // Height of isometric tile (2:1 ratio)
    GRID_SIZE: 7,        // Grid dimensions (10x10 to 7x7)
    MIN_ZOOM: 1,       // Minimum zoom level
    MAX_ZOOM: 3.0,       // Maximum zoom level
    ZOOM_SPEED: 0.001,   // Zoom sensitivity
    PAN_SPEED: 1.0,      // Pan sensitivity
};

/**
 * Isometric coordinate conversion utilities
 */
class IsometricMath {
    /**
     * Convert grid coordinates to screen coordinates
     * @param {number} x - Grid X coordinate
     * @param {number} y - Grid Y coordinate
     * @param {number} tileWidth - Width of a tile
     * @param {number} tileHeight - Height of a tile
     * @returns {Object} Screen coordinates {x, y}
     */
    static gridToScreen(x, y, tileWidth, tileHeight) {
        const screenX = (x - y) * (tileWidth / 2);
        const screenY = (x + y) * (tileHeight / 2);
        return { x: screenX, y: screenY };
    }

    /**
     * Convert screen coordinates to grid coordinates
     * @param {number} screenX - Screen X coordinate
     * @param {number} screenY - Screen Y coordinate
     * @param {number} tileWidth - Width of a tile
     * @param {number} tileHeight - Height of a tile
     * @returns {Object} Grid coordinates {x, y}
     */
    static screenToGrid(screenX, screenY, tileWidth, tileHeight) {
        const x = (screenX / (tileWidth / 2) + screenY / (tileHeight / 2)) / 2;
        const y = (screenY / (tileHeight / 2) - screenX / (tileWidth / 2)) / 2;
        return { x: Math.floor(x), y: Math.floor(y) };
    }
}

/**
 * Camera controller for pan and zoom
 */
class Camera {
    constructor(app) {
        this.app = app;
        this.x = 0;
        this.y = 0;
        this.zoom = 2;
        this.targetZoom = 2;
        this.minZoom = CONFIG.MIN_ZOOM;
        this.maxZoom = CONFIG.MAX_ZOOM;

        // Pan state
        this.isPanning = false;
        this.lastPanX = 0;
        this.lastPanY = 0;

        // Touch state for pinch zoom
        this.touches = new Map();
        this.lastPinchDistance = 0;

        this.setupEvents();
    }

    setupEvents() {
        const canvas = this.app.view;

        // Mouse events
        canvas.addEventListener('mousedown', this.onMouseDown.bind(this));
        canvas.addEventListener('mousemove', this.onMouseMove.bind(this));
        canvas.addEventListener('mouseup', this.onMouseUp.bind(this));
        canvas.addEventListener('mouseleave', this.onMouseUp.bind(this));
        canvas.addEventListener('wheel', this.onWheel.bind(this), { passive: false });

        // Touch events
        canvas.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
        canvas.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
        canvas.addEventListener('touchend', this.onTouchEnd.bind(this));
        canvas.addEventListener('touchcancel', this.onTouchEnd.bind(this));

        // Prevent context menu
        canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    }

    onMouseDown(e) {
        if (e.button === 0 || e.button === 2) {
            this.isPanning = true;
            this.lastPanX = e.clientX;
            this.lastPanY = e.clientY;
        }
    }

    onMouseMove(e) {
        if (this.isPanning) {
            const dx = e.clientX - this.lastPanX;
            const dy = e.clientY - this.lastPanY;

            this.x += dx / this.zoom;
            this.y += dy / this.zoom;

            this.lastPanX = e.clientX;
            this.lastPanY = e.clientY;
        }
    }

    onMouseUp() {
        this.isPanning = false;
    }

    onWheel(e) {
        e.preventDefault();

        const zoomFactor = 1 - e.deltaY * CONFIG.ZOOM_SPEED;
        const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.targetZoom * zoomFactor));

        // Zoom towards mouse position
        const rect = this.app.view.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const worldX = (mouseX - this.x) / this.zoom;
        const worldY = (mouseY - this.y) / this.zoom;

        this.targetZoom = newZoom;

        // Adjust position to zoom towards mouse
        this.x = mouseX - worldX * newZoom;
        this.y = mouseY - worldY * newZoom;
    }

    onTouchStart(e) {
        e.preventDefault();

        for (let touch of e.changedTouches) {
            this.touches.set(touch.identifier, {
                x: touch.clientX,
                y: touch.clientY
            });
        }

        if (this.touches.size === 2) {
            const touchArray = Array.from(this.touches.values());
            this.lastPinchDistance = this.getDistance(touchArray[0], touchArray[1]);
        } else if (this.touches.size === 1) {
            const touch = Array.from(this.touches.values())[0];
            this.isPanning = true;
            this.lastPanX = touch.x;
            this.lastPanY = touch.y;
        }
    }

    onTouchMove(e) {
        e.preventDefault();

        for (let touch of e.changedTouches) {
            if (this.touches.has(touch.identifier)) {
                this.touches.set(touch.identifier, {
                    x: touch.clientX,
                    y: touch.clientY
                });
            }
        }

        if (this.touches.size === 2) {
            // Pinch zoom
            const touchArray = Array.from(this.touches.values());
            const distance = this.getDistance(touchArray[0], touchArray[1]);

            if (this.lastPinchDistance > 0) {
                const scale = distance / this.lastPinchDistance;
                const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.targetZoom * scale));

                // Zoom towards center of pinch
                const centerX = (touchArray[0].x + touchArray[1].x) / 2;
                const centerY = (touchArray[0].y + touchArray[1].y) / 2;
                const rect = this.app.view.getBoundingClientRect();
                const canvasCenterX = centerX - rect.left;
                const canvasCenterY = centerY - rect.top;

                const worldX = (canvasCenterX - this.x) / this.zoom;
                const worldY = (canvasCenterY - this.y) / this.zoom;

                this.targetZoom = newZoom;
                this.x = canvasCenterX - worldX * newZoom;
                this.y = canvasCenterY - worldY * newZoom;
            }

            this.lastPinchDistance = distance;
        } else if (this.touches.size === 1 && this.isPanning) {
            // Pan
            const touch = Array.from(this.touches.values())[0];
            const dx = touch.x - this.lastPanX;
            const dy = touch.y - this.lastPanY;

            this.x += dx / this.zoom;
            this.y += dy / this.zoom;

            this.lastPanX = touch.x;
            this.lastPanY = touch.y;
        }
    }

    onTouchEnd(e) {
        for (let touch of e.changedTouches) {
            this.touches.delete(touch.identifier);
        }

        if (this.touches.size < 2) {
            this.lastPinchDistance = 0;
        }

        if (this.touches.size === 0) {
            this.isPanning = false;
        }
    }

    getDistance(touch1, touch2) {
        const dx = touch1.x - touch2.x;
        const dy = touch1.y - touch2.y;
        return Math.sqrt(dx * dx + dy * dy);
    }

    update() {
        // Smooth zoom interpolation
        const zoomDiff = this.targetZoom - this.zoom;
        if (Math.abs(zoomDiff) > 0.001) {
            this.zoom += zoomDiff * 0.2;
        }
    }

    apply(container) {
        container.position.set(this.x, this.y);
        container.scale.set(this.zoom);
    }
}

/**
 * Floor tile texture mapping for Wang tileset (16 tiles)
 * Based on 4-bit bitmask: N(8) | W(4) | S(2) | E(1)
 */
const WANG_TILE_MAP = {
    0: 'floor.png',   // 0000 - isolated
};

/**
 * Isometric tile with floor texture
 */
class IsometricTile {
    constructor(x, y, width, height, color, tileIndex) {
        this.gridX = x;
        this.gridY = y;
        this.width = width;
        this.height = height;
        this.color = color;
        this.tileIndex = tileIndex;

        // Pre-calculate screen position
        const pos = IsometricMath.gridToScreen(x, y, width, height);
        this.screenX = pos.x;
        this.screenY = pos.y;

        // Sprite for floor texture
        this.sprite = null;
    }

    /**
     * Create floor sprite from texture
     */
    createSprite(texture) {
        if (!texture) return null;

        const halfW = this.width / 2;
        const halfH = this.height / 2;

        // Create a container that holds the masked sprite
        const container = new PIXI.Container();
        container.position.set(this.screenX, this.screenY);
	container.sortableChildren = true;

        // Draw a diamond mask so the texture doesn't overflow the tile
        const mask = new PIXI.Graphics();
        mask.beginFill(0xffffff);
        mask.moveTo(0, -halfH);
        mask.lineTo(halfW, 0);
        mask.lineTo(0, halfH);
        mask.lineTo(-halfW, 0);
        mask.closePath();
        mask.endFill();
        container.addChild(mask);

        this.sprite = new PIXI.Sprite(texture);
        this.sprite.anchor.set(0.5);
        // Scale so the texture covers the diamond area
        const scaleX = this.width / texture.width;
        const scaleY = this.height / texture.height;
        this.sprite.scale.set(scaleX, scaleY);
        this.sprite.mask = mask;
        container.addChild(this.sprite);

        this.container = container;
        return container;
    }
}

/**
 * Isometric grid system with floor tiles
 */
class IsometricGrid {
    constructor(width, height, tileWidth, tileHeight) {
        this.width = width;
        this.height = height;
        this.tileWidth = tileWidth;
        this.tileHeight = tileHeight;
        this.tiles = [];
        this.floorTextures = {};
        this.floorContainer = new PIXI.Container();

        this.generateTiles();
    }

    /**
     * Load floor tile textures
     */
    async loadTextures() {
        const loadPromises = [];

        for (let i = 0; i < 16; i++) {
            const filename = WANG_TILE_MAP[i];
            const path = `/assets/isometric-sprite/${filename}`;

            loadPromises.push(
                PIXI.Assets.load(path)
                    .then(texture => {
                        this.floorTextures[i] = texture;
                    })
                    .catch(error => {
                        console.warn(`[Grid] Failed to load floor tile ${filename}:`, error);
                    })
            );
        }

        await Promise.all(loadPromises);
        console.log('[Grid] Floor textures loaded');
    }

    generateTiles() {
        for (let y = 0; y < this.height; y++) {
            for (let x = 0; x < this.width; x++) {
                // Calculate Wang tile index based on neighbors
                // For now, use a simple pattern that creates variation
                const tileIndex = this.calculateWangIndex(x, y);

                // Create checkerboard fallback color
                const isEven = (x + y) % 2 === 0;
                const color = isEven ? 0x4a5568 : 0x2d3748;

                this.tiles.push(new IsometricTile(x, y, this.tileWidth, this.tileHeight, color, tileIndex));
            }
        }
    }

    /**
     * Calculate Wang tile index based on position
     * Uses a single tile (tuile_15.png - all connections) for the entire floor
     */
    calculateWangIndex(x, y) {
        // Use tile 15 (1111 - all connections) for the entire floor
        return 0;
    }

    /**
     * Create sprites for all tiles
     */
    createSprites() {
        for (const tile of this.tiles) {
            const texture = this.floorTextures[tile.tileIndex];
            if (texture) {
                const sprite = tile.createSprite(texture);
                if (sprite) {
                    this.floorContainer.addChild(sprite);
                }
            }
        }

        // Add a subtle grid overlay for better tile visibility
        const gridGraphics = new PIXI.Graphics();
        gridGraphics.lineStyle(1, 0x1a202c, 0.15);

        for (const tile of this.tiles) {
            const halfWidth = tile.width / 2;
            const halfHeight = tile.height / 2;

            gridGraphics.moveTo(tile.screenX, tile.screenY - halfHeight);
            gridGraphics.lineTo(tile.screenX + halfWidth, tile.screenY);
            gridGraphics.lineTo(tile.screenX, tile.screenY + halfHeight);
            gridGraphics.lineTo(tile.screenX - halfWidth, tile.screenY);
            gridGraphics.closePath();
        }

        this.floorContainer.addChild(gridGraphics);
    }

    addToContainer(container) {
        container.addChildAt(this.floorContainer, 0); // Add at bottom
    }
}

/**
 * Main isometric engine
 */
class IsometricEngine {
    constructor() {
        this.app = null;
        this.camera = null;
        this.grid = null;
        this.worldContainer = null;
        this.agentManager = null;
        this.furnitureManager = null;
        this.walkableGrid = null;
        this.debugOverlay = null;
        this.debugOverlayVisible = false;

        this.init();
    }

    async init() {
        // Initialize PIXI Application
        this.app = new PIXI.Application({
            view: document.getElementById('game-canvas'),
            resizeTo: window,
            backgroundColor: 0x1a1a2e,
            antialias: true,
            resolution: window.devicePixelRatio || 1,
            autoDensity: true,
            powerPreference: 'high-performance'
        });

        // Create world container with proper layer order
        this.worldContainer = new PIXI.Container();
        this.app.stage.addChild(this.worldContainer);

        // Create layers: floor (statique) + dynamic (personnages + meubles ensemble)
        this.floorLayer = new PIXI.Container();
        this.dynamicLayer = new PIXI.Container();
        
        // Activer le tri automatique par zIndex sur le layer dynamique
        this.dynamicLayer.sortableChildren = true;
        
        this.worldContainer.addChild(this.floorLayer);
        this.worldContainer.addChild(this.dynamicLayer);

        // Initialize camera
        this.camera = new Camera(this.app);

        // Center camera initially - center the isometric grid on screen
        const gridCenterX = (CONFIG.GRID_SIZE - 1) / 2;
        const gridCenterY = (CONFIG.GRID_SIZE - 1) / 2;
        const centerScreenX = (gridCenterX - gridCenterY) * (CONFIG.TILE_WIDTH / 2);
        const centerScreenY = (gridCenterX + gridCenterY) * (CONFIG.TILE_HEIGHT / 2);
        
        this.camera.x = this.app.screen.width / 2 - centerScreenX * this.camera.zoom;
        this.camera.y = this.app.screen.height / 2 - centerScreenY * this.camera.zoom;

        // Initialize furniture manager with dynamic layer so furniture and characters share the same container
        this.furnitureManager = new FurnitureManager(CONFIG.TILE_WIDTH, CONFIG.TILE_HEIGHT, this.dynamicLayer);

        // Load textures first
        await this.loadAssets();

        // Create isometric grid
        this.grid = new IsometricGrid(
            CONFIG.GRID_SIZE,
            CONFIG.GRID_SIZE,
            CONFIG.TILE_WIDTH,
            CONFIG.TILE_HEIGHT
        );
        await this.grid.loadTextures();
        this.grid.createSprites();
        this.grid.addToContainer(this.floorLayer);

        // Add office furniture
        this.placeOfficeFurniture();

        // Initialize walkable grid for collision detection
        this.initializeWalkableGrid();

        // Initialize multiple characters
        this.characters = [];
        this.characterMap = new Map(); // Map character names to character objects
        this.spawnCharacters();

        // Setup debug overlay toggle
        this.setupDebugToggle();

        // Initialize agent manager with callback to update characters
        this.agentManager = new AgentManager((agentData) => {
            this.handleAgentUpdate(agentData);
        });

        // Start render loop
        this.app.ticker.add(this.update.bind(this));

        // Handle window resize
        window.addEventListener('resize', this.onResize.bind(this));
    }

    /**
     * Load all assets
     */
    async loadAssets() {
        console.log('[Engine] Loading assets...');
        await this.furnitureManager.loadAll();
        console.log('[Engine] Assets loaded');
    }

    /**
     * Place all office furniture according to the layout configuration
     */
    placeOfficeFurniture() {
        console.log('[Engine] Placing office furniture...');

        // Create walls first (they go behind furniture)
        this.wallContainer = new PIXI.Container();
        this.createWalls();
        this.floorLayer.addChild(this.wallContainer);

        // Place all furniture groups
        for (const group of OFFICE_FURNITURE_LAYOUT) {
            console.log(`[Engine] Placing ${group.group}...`);
            for (const item of group.items) {
                this.furnitureManager.place(
                    item.type,
                    item.x,
                    item.y,
                    item.orientation,
                    item.options || {}
                );
            }
        }

        // Furniture is now added directly to dynamic layer, no need to add container again
        // Initial sort of all dynamic objects
        this.sortByDepth();

        console.log('[Engine] Furniture placement complete');
    }

    initializeWalkableGrid() {
        console.log('[Engine] Initializing walkable grid from matrix...');
        
        this.walkableGrid = new WalkableGrid(CONFIG.GRID_SIZE, CONFIG.GRID_SIZE);
        this.walkableGrid.initializeFromMatrix(WALKABLE_MATRIX);
        
        console.log('[Engine] Walkable grid initialized');
        console.log('[Engine] Press "D" to toggle debug overlay');
    }

    setupDebugToggle() {
        window.addEventListener('keydown', (e) => {
            if (e.key === 'd' || e.key === 'D') {
                this.toggleDebugOverlay();
            }
        });
    }

    toggleDebugOverlay() {
        if (!this.debugOverlay) {
            this.debugOverlay = this.walkableGrid.createDebugOverlay(CONFIG.TILE_WIDTH, CONFIG.TILE_HEIGHT);
            this.floorLayer.addChild(this.debugOverlay);
        }
        
        this.debugOverlayVisible = !this.debugOverlayVisible;
        this.debugOverlay.visible = this.debugOverlayVisible;
        
        console.log(`[Engine] Debug overlay: ${this.debugOverlayVisible ? 'visible' : 'hidden'}`);
    }

    /**
     * Furniture definitions with desk positions
     * All positioned within 10x10 grid
     * NOTE: Deprecated - use OFFICE_FURNITURE_LAYOUT instead
     */
    furnitureDefinitions = [
        // Zone 1: Red - Alex (CEO)
        { x: 2, y: 2, type: 'desk', zone: 'red', size: 'full' },

        // Zone 2: Blue - Bob (Lead Dev)
        { x: 7, y: 2, type: 'desk', zone: 'blue', size: 'full' },

        // Zone 3: Green - Carl (COO)
        { x: 2, y: 7, type: 'desk', zone: 'green', size: 'full' }
    ];

    // Wall definitions - Trimmed border for 7x7 grid (3 walls each side)
    wallDefinitions = [
        // North border (top edge): y=0, x=0-6 (7 walls)
        { x: 0, y: 0, type: 'wall', orientation: 'corner' },
        { x: 1, y: 0, type: 'wall', orientation: 'vertical' },
        { x: 2, y: 0, type: 'wall', orientation: 'vertical' },
        { x: 3, y: 0, type: 'wall', orientation: 'vertical' },
        { x: 4, y: 0, type: 'wall', orientation: 'vertical' },
        { x: 5, y: 0, type: 'wall', orientation: 'vertical' },
        { x: 6, y: 0, type: 'wall', orientation: 'vertical' },

        // West border (left edge): x=0, y=1-6 (6 walls)
        { x: 0, y: 1, type: 'wall', orientation: 'horizontal' },
        { x: 0, y: 2, type: 'wall', orientation: 'horizontal' },
        { x: 0, y: 3, type: 'wall', orientation: 'horizontal' },
        { x: 0, y: 4, type: 'wall', orientation: 'horizontal' },
        { x: 0, y: 5, type: 'wall', orientation: 'horizontal' },
        { x: 0, y: 6, type: 'wall', orientation: 'horizontal' }
    ];

    /**
     * Create walls around top-left and top-right corners
     */
    createWalls() {
        const wallGraphics = new PIXI.Graphics();
        const wallColor = 0xf8f8f1; // Gray wall color
        const wallHeight = 100; // Height of walls

        for (const wall of this.wallDefinitions) {
            const pos = IsometricMath.gridToScreen(wall.x, wall.y, CONFIG.TILE_WIDTH, CONFIG.TILE_HEIGHT);

            if (wall.orientation === 'horizontal') {
                // Horizontal wall segment (runs along top edge of tile)
                this.drawWallSegment(wallGraphics, pos.x, pos.y, 'horizontal', wallColor, wallHeight);
            } else if (wall.orientation === 'vertical') {
                // Vertical wall segment (runs along left edge of tile)
                this.drawWallSegment(wallGraphics, pos.x, pos.y, 'vertical', wallColor, wallHeight);
            } else if (wall.orientation === 'corner') {
                // Corner wall (L-shape)
                this.drawWallCorner(wallGraphics, pos.x, pos.y, wallColor, wallHeight);
            }
        }

        this.wallContainer.addChild(wallGraphics);
    }

    /**
     * Draw a single wall segment
     * Walls are positioned at the OUTER edge of the tile, not at the center.
     * 'horizontal' walls run along the north edge (top-right iso edge) of y=0 tiles.
     * 'vertical' walls run along the west edge (top-left iso edge) of x=0 tiles.
     */
    drawWallSegment(graphics, x, y, orientation, color, height) {
        const halfW = CONFIG.TILE_WIDTH / 2;
        const halfH = CONFIG.TILE_HEIGHT / 2;

        if (orientation === 'horizontal') {
            // North edge of tile: from top-left corner to top vertex
            // In iso, the top-left corner of tile (x,y) is at (screenX - halfW, screenY)
            // and the top vertex is at (screenX, screenY - halfH)
            const x0 = x - halfW; // left corner of diamond
            const y0 = y;         // left corner of diamond
            const x1 = x;         // top vertex of diamond
            const y1 = y - halfH; // top vertex of diamond

            // Front face (west border faces away from viewer → darker)
            graphics.beginFill(this.darkenColor(color, 0.9));
            graphics.moveTo(x0, y0);
            graphics.lineTo(x1, y1);
            graphics.lineTo(x1, y1 - height);
            graphics.lineTo(x0, y0 - height);
            graphics.closePath();
            graphics.endFill();

            // Top face of wall
            graphics.beginFill(this.darkenColor(color, 0.9));
            graphics.moveTo(x0, y0 - height);
            graphics.lineTo(x1, y1 - height);
            graphics.lineTo(x1 + 2, y1 - height - 1);
            graphics.lineTo(x0 + 2, y0 - height - 1);
            graphics.closePath();
            graphics.endFill();
        } else {
            // West edge of tile: from top vertex to right corner
            // Top vertex: (screenX, screenY - halfH)
            // Right corner: (screenX + halfW, screenY)
            const x0 = x;         // top vertex of diamond
            const y0 = y - halfH; // top vertex of diamond
            const x1 = x + halfW; // right corner of diamond
            const y1 = y;         // right corner of diamond

            // Front face (north border faces the viewer → lighter)
            graphics.beginFill(this.lightenColor(color, 0.9));
            graphics.moveTo(x0, y0);
            graphics.lineTo(x1, y1);
            graphics.lineTo(x1, y1 - height);
            graphics.lineTo(x0, y0 - height);
            graphics.closePath();
            graphics.endFill();

            // Top face of wall
            graphics.beginFill(this.lightenColor(color, 0.9));
            graphics.moveTo(x0, y0 - height);
            graphics.lineTo(x1, y1 - height);
            graphics.lineTo(x1 - 2, y1 - height - 1);
            graphics.lineTo(x0 - 2, y0 - height - 1);
            graphics.closePath();
            graphics.endFill();
        }
    }

    /**
     * Draw a wall corner (L-shape) at the intersection of north and west borders
     */
    drawWallCorner(graphics, x, y, color, height) {
        // The corner is at tile (0,0). We draw both the north edge and the west edge walls.
        // North edge (horizontal part)
        this.drawWallSegment(graphics, x, y, 'horizontal', color, height);
        // West edge (vertical part)
        this.drawWallSegment(graphics, x, y, 'vertical', color, height);
    }

    /**
     * Lighten a color
     */
    lightenColor(color, factor) {
        const r = Math.min(255, Math.floor(((color >> 16) & 0xff) / factor));
        const g = Math.min(255, Math.floor(((color >> 8) & 0xff) / factor));
        const b = Math.min(255, Math.floor((color & 0xff) / factor));
        return (r << 16) | (g << 8) | b;
    }

    /**
     * Get color for zone
     */
    getZoneColor(zone) {
        const colors = {
            red: 0xe53e3e,
            blue: 0x3182ce,
            green: 0x38a169,
            yellow: 0xd69e2e,
            purple: 0x805ad5,
            orange: 0xdd6b20
        };
        return colors[zone] || 0x666666;
    }

    spawnCharacters() {
        const characterConfigs = [
            {
                name: 'Alex',
                role: 'CEO',
                startGridX: 2,
                startGridY: 2,
                scale: 1.3,
                color: 0xff6b6b,
                spriteSheet: 'human.png',
                furnitureZone: 'red',
                furniturePositions: [{ x: 2, y: 2 }]
            },
            {
                name: 'Carl',
                role: 'COO',
                startGridX: 2,
                startGridY: 5,
                scale: 1.3,
                color: 0x90ee90,
                spriteSheet: 'human2.png',
                furnitureZone: 'green',
                furniturePositions: [{ x: 2, y: 5 }]
            },
            {
                name: 'Bob',
                role: 'Lead Dev',
                startGridX: 5,
                startGridY: 2,
                scale: 1.3,
                color: 0x87ceeb,
                spriteSheet: 'human3.png',
                furnitureZone: 'blue',
                furniturePositions: [{ x: 5, y: 2 }]
            }
        ];

        for (const config of characterConfigs) {
            const character = new IsometricCharacter({
                worldContainer: this.dynamicLayer,
                tileWidth: CONFIG.TILE_WIDTH,
                tileHeight: CONFIG.TILE_HEIGHT,
                name: config.name,
                role: config.role,
                startGridX: config.startGridX,
                startGridY: config.startGridY,
                scale: config.scale,
                color: config.color || null,
                spriteSheet: config.spriteSheet,
                furnitureZone: config.furnitureZone,
                furniturePositions: config.furniturePositions,
                walkableGrid: this.walkableGrid
            });

            this.characters.push(character);
            this.characterMap.set(config.name, character);
        }

        console.log(`[Engine] Spawned ${this.characters.length} characters`);
    }

    /**
     * Handle agent status updates from Convex
     */
    handleAgentUpdate(agentData) {
        // Find matching character by name
        const character = this.characterMap.get(agentData.name);
        if (character) {
            character.updateAgentStatus(agentData);
        }
    }

    drawCube(graphics, x, y, tileWidth, tileHeight, height, color) {
        const halfWidth = tileWidth / 2;
        const halfHeight = tileHeight / 2;

        // Top face
        graphics.beginFill(color);
        graphics.lineStyle(1, 0x000000, 0.2);
        graphics.moveTo(x, y - halfHeight);
        graphics.lineTo(x + halfWidth, y);
        graphics.lineTo(x, y + halfHeight);
        graphics.lineTo(x - halfWidth, y);
        graphics.closePath();
        graphics.endFill();

        // Right face (darker)
        graphics.beginFill(this.darkenColor(color, 0.8));
        graphics.moveTo(x + halfWidth, y);
        graphics.lineTo(x, y + halfHeight);
        graphics.lineTo(x, y + halfHeight + height);
        graphics.lineTo(x + halfWidth, y + height);
        graphics.closePath();
        graphics.endFill();

        // Left face (lighter)
        graphics.beginFill(this.darkenColor(color, 0.6));
        graphics.moveTo(x - halfWidth, y);
        graphics.lineTo(x, y + halfHeight);
        graphics.lineTo(x, y + halfHeight + height);
        graphics.lineTo(x - halfWidth, y + height);
        graphics.closePath();
        graphics.endFill();
    }

    darkenColor(color, factor) {
        const r = Math.floor(((color >> 16) & 0xff) * factor);
        const g = Math.floor(((color >> 8) & 0xff) * factor);
        const b = Math.floor((color & 0xff) * factor);
        return (r << 16) | (g << 8) | b;
    }

    update(delta) {
        // Update camera
        this.camera.update();
        this.camera.apply(this.worldContainer);

        // Update all characters
        for (const character of this.characters) {
            character.setCameraZoom(this.camera.zoom);
            character.update(delta);
        }

        // Z-sort characters and furniture by depth (every few frames for performance)
        this.zSortTimer = (this.zSortTimer || 0) + delta;
        if (this.zSortTimer > 5) { // Sort every 5 frames
            this.sortByDepth();
            this.zSortTimer = 0;
        }

        // Update zoom display
        const zoomPercent = Math.round(this.camera.zoom * 100);
        const zoomDisplay = document.getElementById('zoom-display');
        if (zoomDisplay) {
            zoomDisplay.textContent = `Zoom: ${zoomPercent}%`;
        }
    }

    /**
     * Sort all dynamic objects by depth for proper isometric rendering
     * Items with higher (gridX + gridY) should be rendered last (on top)
     * Characters have zOffset=1, so they appear between furniture (zOffset=0) and chairs (zOffset=2)
     */
    sortByDepth() {
        // Force immediate sort of the dynamic layer
        // This sorts all children (characters AND furniture) together by zIndex
        if (this.dynamicLayer) {
            this.dynamicLayer.sortChildren();
        }
    }

    onResize() {
        this.app.renderer.resize(window.innerWidth, window.innerHeight);
    }
}

/**
 * PostMessage Receiver
 * Receives messages and agents data from parent window via postMessage
 */
class PostMessageReceiver {
    constructor() {
        this.agents = new Map();
        this.seenMessageIds = new Set();
        this.initialMessagesLoaded = false;

        this.setupListener();
    }

    setupListener() {
        window.addEventListener('message', (event) => {
            // Security check
            const allowedOrigins = [
                'https://unhumanlabs.xyz',
                'https://www.unhumanlabs.xyz',
                'http://localhost:3000'
            ];
            
            if (!allowedOrigins.includes(event.origin) && !event.origin.endsWith('.vercel.app') && !event.origin.endsWith('.unhumanlabs.xyz')) {
                console.warn('[PostMessageReceiver] Blocked message from unauthorized origin:', event.origin);
                return;
            }

            console.log('[PostMessageReceiver] Received message from origin:', event.origin);
            console.log('[PostMessageReceiver] Received message data:', event.data);
            const { type, data, payload } = event.data || {};

            if (type === 'SSE_UPDATE' && payload) {
                const { messages, agents } = payload;
                if (agents && Array.isArray(agents)) {
                    console.log('[PostMessageReceiver] Processing SSE_UPDATE for agents:', agents.length);
                    this.handleAgentsUpdate(agents);
                }
                if (messages && Array.isArray(messages)) {
                    console.log('[PostMessageReceiver] Processing SSE_UPDATE for messages:', messages.length);
                    this.handleMessagesUpdate(messages);
                }
                return;
            }

            // Legacy support
            if (type === 'AGENTS_UPDATE' && Array.isArray(data)) {
                console.log('[PostMessageReceiver] Processing AGENTS_UPDATE:', data.length, 'agents');
                this.handleAgentsUpdate(data);
            }

            if (type === 'MESSAGES_UPDATE' && Array.isArray(data)) {
                console.log('[PostMessageReceiver] Processing MESSAGES_UPDATE:', data.length, 'messages');
                this.handleMessagesUpdate(data);
            }
        });

        console.log('[PostMessageReceiver] Ready - waiting for data from parent');
        
        // Notify the parent window that PixiJS is fully loaded and ready to receive SSE events
        if (window.parent !== window) {
            window.parent.postMessage({ type: 'PIXI_READY' }, '*');
        }
    }

    handleAgentsUpdate(agents) {
        console.log('[PostMessageReceiver] Agents updated:', agents);
        this.agents.clear();
        agents.forEach(agent => {
            this.agents.set(agent.id, agent);
            if (engineInstance && engineInstance.agentManager) {
                engineInstance.agentManager.handleExternalAgentUpdate(agent);
            }
        });
    }

    handleMessagesUpdate(messages) {
        console.log('[PostMessageReceiver] Messages updated:', messages);
        
        if (!messages || messages.length === 0) return;

        // On first load, show bubbles for the most recent messages (last 3)
        if (!this.initialMessagesLoaded) {
            console.log('[PostMessageReceiver] Initial load - showing recent messages');
            const recentMessages = messages.slice(-3); // Show last 3 messages
            recentMessages.forEach(msg => {
                if (!this.seenMessageIds.has(msg.id)) {
                    this.seenMessageIds.add(msg.id);
                    this.showMessageBubble(msg);
                }
            });
            this.initialMessagesLoaded = true;
            return;
        }

        // After initial load, only show bubbles for new messages
        messages.forEach(msg => {
            if (!this.seenMessageIds.has(msg.id)) {
                this.seenMessageIds.add(msg.id);
                console.log('[PostMessageReceiver] New message detected:', msg);
                this.showMessageBubble(msg);
            }
        });
        
        // Clean up old seen IDs to prevent memory bloat
        if (this.seenMessageIds.size > 1000) {
            const recentIds = new Set(
                messages.slice(-200).map(m => m.id)
            );
            this.seenMessageIds = recentIds;
        }
    }

    showMessageBubble(msg) {
        if (!engineInstance || !engineInstance.characterMap) {
            console.warn('[PostMessageReceiver] Engine or characterMap not ready');
            return;
        }

        const agent = this.agents.get(msg.fromAgentId);
        const authorName = agent ? agent.name : msg.fromAgentName || 'Unknown';
        console.log('[PostMessageReceiver] Showing bubble for:', authorName, 'content:', msg.content);

        const character = engineInstance.characterMap.get(authorName);
        if (character) {
            const content = msg.content || msg.text || '';
            if (content) {
                character.showBubble(content);
            }
        } else {
            console.warn('[PostMessageReceiver] Character not found:', authorName, 'Available:', [...engineInstance.characterMap.keys()]);
        }
    }
}

// Initialize engine when DOM is ready
let engineInstance = null;
document.addEventListener('DOMContentLoaded', () => {
    engineInstance = new IsometricEngine();
    new PostMessageReceiver();
});

